import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ShieldCheck, IndianRupee } from "lucide-react";
import { api, apiError, money, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const thisMonth = () => new Date().toISOString().slice(0, 7);

const VAL_TONE = {
  validated: "bg-emerald-50 text-emerald-700 border-emerald-200",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  disputed: "bg-red-50 text-red-700 border-red-200",
};
const PAY_TONE = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  unpaid: "bg-red-50 text-red-700 border-red-200",
};

const Field = ({ label, children }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
    {children}
  </div>
);

const Kpi = ({ label, value, tone, icon: Icon, testid }) => (
  <div className="border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="flex items-start justify-between">
      <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      {Icon && <Icon className="h-4 w-4 text-slate-400" />}
    </div>
    <div className={`mt-3 font-display text-2xl font-black tracking-tight ${tone || ""}`}>{value}</div>
  </div>
);

export default function Validation() {
  const { masters } = useData();
  const { can } = useAuth();
  const editable = can("validation", "edit");
  const [period, setPeriod] = useState(thisMonth());
  const [client, setClient] = useState("all");
  const [campaign, setCampaign] = useState("all");
  const [vFilter, setVFilter] = useState("all");
  const [pFilter, setPFilter] = useState("all");
  const [data, setData] = useState({ rows: [], totals: {} });

  const load = useCallback(async () => {
    const { data } = await api.get("/validation", {
      params: {
        period,
        client_id: client,
        campaign_id: campaign,
        validation_status: vFilter,
        payment_status: pFilter,
      },
    });
    setData(data);
  }, [period, client, campaign, vFilter, pFilter]);

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [load]);

  const patch = async (row, changes) => {
    try {
      await api.put("/validation", {
        period: row.period,
        client_id: row.client_id,
        campaign_id: row.campaign_id,
        validation_status: row.validation_status,
        validate_date: row.validate_date || "",
        payment_status: row.payment_status,
        payment_date: row.payment_date || "",
        amount_received: Number(row.amount_received) || 0,
        notes: row.notes || "",
        ...changes,
      });
      setData((d) => ({
        ...d,
        rows: d.rows.map((r) => (r.key === row.key ? { ...r, ...changes } : r)),
      }));
      toast.success("Saved");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const exportXlsx = () => {
    const token = localStorage.getItem("adsdesk_token");
    fetch(`${API}/export/validation?period=${period}&client_id=${client}&campaign_id=${campaign}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.blob() : Promise.reject(r)))
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `validation-${period}.xlsx`;
        a.click();
        toast.success("Excel downloaded");
      })
      .catch(() => toast.error("Could not export"));
  };

  const t = data.totals || {};

  return (
    <div data-testid="validation-page">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Monthly · {data.period || period}
          </div>
          <h1 className="mt-2 font-display text-4xl font-black tracking-tight sm:text-5xl">
            Validation &amp; Payment
          </h1>
          <p className="mt-3 max-w-2xl text-sm text-slate-600">
            Confirm each client and campaign month by month, set the validate date and payment date,
            and record what has actually been received.
          </p>
        </div>
        <Button
          variant="outline"
          data-testid="validation-export-button"
          className="rounded-md border-slate-300 active:scale-95"
          onClick={exportXlsx}
        >
          <Download className="mr-2 h-4 w-4" /> Export Excel
        </Button>
      </div>

      <div
        className="mb-8 grid grid-cols-2 gap-3 border border-slate-200 bg-slate-50 p-4 md:grid-cols-5"
        data-testid="validation-filters"
      >
        <Field label="Month">
          <Input
            type="month"
            data-testid="validation-period-input"
            className="h-9 border-slate-300 bg-white"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          />
        </Field>
        <Field label="Client">
          <Select value={client} onValueChange={setClient}>
            <SelectTrigger data-testid="validation-client-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">All Clients</SelectItem>
              {masters.clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Campaign">
          <Select value={campaign} onValueChange={setCampaign}>
            <SelectTrigger data-testid="validation-campaign-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              <SelectItem value="all">All Campaigns</SelectItem>
              {masters.campaigns.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Validation">
          <Select value={vFilter} onValueChange={setVFilter}>
            <SelectTrigger data-testid="validation-status-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              {["all", "pending", "validated", "disputed"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Payment">
          <Select value={pFilter} onValueChange={setPFilter}>
            <SelectTrigger data-testid="validation-payment-filter" className="h-9 border-slate-300 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border border-slate-200 bg-white">
              {["all", "unpaid", "partial", "paid"].map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Spent" value={money(t.spend)} icon={IndianRupee} testid="validation-kpi-spend" />
        <Kpi label="Actual Revenue" value={money(t.actual_revenue)} testid="validation-kpi-actual" />
        <Kpi
          label="Margin"
          value={money(t.margin)}
          tone={(t.margin || 0) >= 0 ? "text-emerald-600" : "text-red-600"}
          testid="validation-kpi-margin"
        />
        <Kpi label="Received" value={money(t.received)} icon={ShieldCheck} testid="validation-kpi-received" />
      </div>

      <div className="overflow-x-auto border border-slate-200 bg-white">
        <table className="w-full min-w-[1500px] text-sm" data-testid="validation-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">
              <th className="px-3 py-3">Client Name</th>
              <th className="px-3 py-3">Campaign</th>
              <th className="px-3 py-3 text-right">Spent</th>
              <th className="px-3 py-3 text-right">Revenue</th>
              <th className="px-3 py-3 text-right">Actual Revenue</th>
              <th className="px-3 py-3 text-right">Margin</th>
              <th className="px-3 py-3">Validation</th>
              <th className="px-3 py-3">Validate Date</th>
              <th className="px-3 py-3">Payment</th>
              <th className="px-3 py-3">Payment Date</th>
              <th className="px-3 py-3 text-right">Received</th>
              <th className="px-3 py-3">Note</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.rows.map((r) => (
              <tr key={r.key} className="transition-colors hover:bg-slate-50" data-testid={`validation-row-${r.key}`}>
                <td className="px-3 py-2 font-semibold">{r.client}</td>
                <td className="px-3 py-2">{r.campaign}</td>
                <td className="px-3 py-2 text-right">{money(r.spend, r.currency)}</td>
                <td className="px-3 py-2 text-right">{money(r.revenue, r.currency)}</td>
                <td className="px-3 py-2 text-right">{money(r.actual_revenue, r.currency)}</td>
                <td
                  className={`px-3 py-2 text-right font-bold ${
                    r.margin >= 0 ? "text-emerald-600" : "text-red-600"
                  }`}
                >
                  {money(r.margin, r.currency)}
                </td>
                <td className="px-3 py-2">
                  {editable ? (
                    <Select
                      value={r.validation_status}
                      onValueChange={(v) => patch(r, { validation_status: v })}
                    >
                      <SelectTrigger
                        data-testid={`validation-status-${r.key}`}
                        className="h-9 w-32 border-slate-300 bg-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border border-slate-200 bg-white">
                        {["pending", "validated", "disputed"].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={`rounded-md border ${VAL_TONE[r.validation_status]}`}>
                      {r.validation_status}
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="date"
                    disabled={!editable}
                    data-testid={`validate-date-${r.key}`}
                    className="h-9 w-40 border-slate-300"
                    value={r.validate_date || ""}
                    onChange={(e) => patch(r, { validate_date: e.target.value })}
                  />
                </td>
                <td className="px-3 py-2">
                  {editable ? (
                    <Select value={r.payment_status} onValueChange={(v) => patch(r, { payment_status: v })}>
                      <SelectTrigger
                        data-testid={`payment-status-${r.key}`}
                        className="h-9 w-28 border-slate-300 bg-white"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="border border-slate-200 bg-white">
                        {["unpaid", "partial", "paid"].map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge variant="outline" className={`rounded-md border ${PAY_TONE[r.payment_status]}`}>
                      {r.payment_status}
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="date"
                    disabled={!editable}
                    data-testid={`payment-date-${r.key}`}
                    className="h-9 w-40 border-slate-300"
                    value={r.payment_date || ""}
                    onChange={(e) => patch(r, { payment_date: e.target.value })}
                  />
                </td>
                <td className="px-3 py-2">
                  <Input
                    type="number"
                    disabled={!editable}
                    data-testid={`amount-received-${r.key}`}
                    className="h-9 w-32 border-slate-300 text-right"
                    defaultValue={r.amount_received || 0}
                    onBlur={(e) => patch(r, { amount_received: Number(e.target.value) || 0 })}
                  />
                </td>
                <td className="px-3 py-2 min-w-[220px]">
                  <Input
                    disabled={!editable}
                    data-testid={`validation-note-${r.key}`}
                    placeholder="Add a note…"
                    className="h-9 border-transparent bg-transparent px-2 hover:border-slate-300 focus:border-slate-900 focus:bg-white"
                    defaultValue={r.notes || ""}
                    onBlur={(e) => patch(r, { notes: e.target.value })}
                  />
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr>
                <td colSpan={12} className="px-3 py-10 text-center text-slate-500">
                  No campaigns ran in this month with the chosen filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
